import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useHistory } from "react-router-dom";
import { __ } from "services/Translator";
import RewardLine from "components/goldenCloverHub/RewardLine";
import HubToast, { showHubToast } from "components/goldenCloverHub/HubToast";
import {
  CHALLENGE_DETAILS,
  getMissionsMockState,
} from "./missionsMockData";
import { rewardSummary } from "./missionsNormalize";
import {
  BoltIcon,
  FlameIcon,
  CloverIcon,
  GiftIcon,
} from "./missionIcons";

const RING_CIRC = 339.29;
const LOG_TABS = [
  { key: "all", label: __("All"), filter: () => true },
  { key: "active", label: __("Active"), filter: (item) => item.state === "active" },
  { key: "claim", label: __("Claim"), filter: (item) => item.state === "claim" },
  { key: "new", label: __("New"), filter: (item) => item.state === "new" },
  { key: "done", label: __("Done"), filter: (item) => item.state === "done" },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function TierDot({ tier }) {
  return <span className={`tier-dot ${tier}`} aria-hidden="true" />;
}

function TierBadge({ tier, label }) {
  return <span className={`tier-badge ${tier}`}>{label}</span>;
}

function DetailPanel({ challenge, onCta, canOptIn }) {
  if (!challenge) return null;
  const progStyle = challenge.progDone
    ? { width: `${challenge.progPct}%`, background: "linear-gradient(90deg,var(--win),#9fefb8)" }
    : { width: `${challenge.progPct}%` };
  const lockedForGood = challenge.locked && !canOptIn;

  return (
    <section className="vip_club_area detail" id="detail-panel">
      <div className="detail-art">
        <div className="detail-head">
          <TierBadge tier={challenge.tier} label={challenge.tierLabel} />
          <span className={`time ${challenge.timeUrgent ? "urgent" : ""}`}>{challenge.time}</span>
        </div>
      </div>
      <div className="detail-body">
        <div className="detail-row">
          <div className="detail-pic" style={{ background: challenge.artBg }} />
          <div className="detail-text">
            <div className="detail-game-name">{challenge.game}</div>
            <div className="detail-title" id="mission-detail-title">{challenge.title}</div>
          </div>
        </div>
        <p
          className="detail-cond"
          dangerouslySetInnerHTML={{ __html: challenge.desc }}
        />
        <div className="detail-prog">
          <div className="lab">
            <span>{challenge.progLabel}</span>
            <b>{challenge.progValue}</b>
          </div>
          <div
            className="bar"
            role="progressbar"
            aria-valuenow={Math.round(challenge.progPct || 0)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${challenge.progLabel}: ${challenge.progValue}`}
          >
            <b style={progStyle} />
          </div>
        </div>
        <div className="detail-cta">
          <button
            type="button"
            className={`btn btn-lg ${lockedForGood ? "btn-disabled" : "btn-primary"}`}
            id="detail-play"
            onClick={onCta}
            disabled={lockedForGood}
          >
            {challenge.locked
              ? canOptIn
                ? __("Join challenge")
                : `🔒 ${challenge.cta}`
              : challenge.cta}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function MissionHubContent({
  data: dataProp,
  isPreviewDemo,
  onChallengeAction,
}) {
  const history = useHistory();
  const mock = useMemo(() => getMissionsMockState(), []);
  const data = dataProp || mock;
  const preview = isPreviewDemo ?? data.isPreviewDemo;

  const [logItems, setLogItems] = useState(data.log || []);
  const [selectedCid, setSelectedCid] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [sortMode, setSortMode] = useState("ending");
  const [claimedIds, setClaimedIds] = useState({});
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const lastFocusedItemRef = useRef(null);
  const modalCloseBtnRef = useRef(null);
  const modalShellRef = useRef(null);

  // A claim/opt-in response replaces the hub payload; keep the list in sync.
  useEffect(() => {
    setLogItems(data.log || []);
  }, [data.log]);

  const header = data.header || mock.header;
  const streak = data.streak || mock.streak;
  // Never blend fixture social proof into production. Only fall back to demo
  // claims in preview mode; a live hub with no claim events hides the strip.
  const liveClaims = data.claims || (preview ? mock.claims : null);
  const challenges = data.challenges || CHALLENGE_DETAILS;

  const selectedChallenge = challenges[selectedCid];

  const tabCounts = useMemo(
    () => ({
      all: logItems.length,
      active: logItems.filter((i) => i.state === "active").length,
      claim: logItems.filter((i) => i.state === "claim" && !claimedIds[i.cid]).length,
      new: logItems.filter((i) => i.state === "new").length,
      done: logItems.filter((i) => i.state === "done").length,
    }),
    [logItems, claimedIds]
  );

  // Surface a single "next best action": claimable > near-complete > urgent.
  const featured = useMemo(() => {
    const claimable = logItems.find(
      (i) => i.state === "claim" && !claimedIds[i.cid]
    );
    if (claimable) return { item: claimable, kind: "claim" };
    const active = logItems
      .filter((i) => i.state === "active")
      .sort((a, b) => (b.progPct || 0) - (a.progPct || 0));
    const near = active[0];
    if (near && (near.progPct || 0) >= 90) return { item: near, kind: "near" };
    const urgent = logItems.find((i) => i.timeUrgent && i.state === "active");
    if (urgent) return { item: urgent, kind: "urgent" };
    return near ? { item: near, kind: "near" } : null;
  }, [logItems, claimedIds]);

  const filteredItems = useMemo(() => {
    const tab = LOG_TABS.find((t) => t.key === activeTab);
    let items = logItems.filter((item) => (tab ? tab.filter(item) : true));
    if (activeTab === "claim") {
      items = items.filter((item) => !claimedIds[item.cid]);
    }
    const sorted = [...items];
    sorted.sort((a, b) => {
      const ca = challenges[a.cid] || {};
      const cb = challenges[b.cid] || {};
      if (sortMode === "reward") {
        const ra =
          parseInt(String(a.rewards?.[0]?.amount ?? "0").replace(/\D/g, ""), 10) || 0;
        const rb =
          parseInt(String(b.rewards?.[0]?.amount ?? "0").replace(/\D/g, ""), 10) || 0;
        return rb - ra;
      }
      if (sortMode === "closest") {
        return (cb.progPct || b.progPct || 0) - (ca.progPct || a.progPct || 0);
      }
      if (sortMode === "newest") {
        return (a.state === "new" ? -1 : 1) - (b.state === "new" ? -1 : 1);
      }
      const ua = ca.timeUrgent || a.timeUrgent ? -1 : 1;
      const ub = cb.timeUrgent || b.timeUrgent ? -1 : 1;
      return ua - ub;
    });
    return sorted;
  }, [logItems, activeTab, sortMode, challenges, claimedIds]);

  const closeDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedCid(null);
    const lastFocusedItem = lastFocusedItemRef.current;
    if (lastFocusedItem && typeof lastFocusedItem.focus === "function") {
      lastFocusedItem.focus();
    }
  }, []);

  const selectItem = useCallback((item, focusTarget) => {
    if (focusTarget) {
      lastFocusedItemRef.current = focusTarget;
    }
    setSelectedCid(item.cid);
    setIsDetailModalOpen(true);
  }, []);

  const showActionError = useCallback((message, title) => {
    Swal.fire(title, message || __("Please try again later."), "error");
  }, []);

  const handleClaim = useCallback(
    async (e, item) => {
      e.stopPropagation();
      if (onChallengeAction) {
        if (actionBusy) return;
        setActionBusy(true);
        const result = await onChallengeAction(item.cid, "claim");
        setActionBusy(false);
        if (result?.ok) {
          showHubToast(
            __("Reward claimed"),
            rewardSummary(item.rewards) || __("Added to your balance")
          );
        } else {
          showActionError(result?.message, __("Claim failed"));
        }
        return;
      }
      setClaimedIds((prev) => ({ ...prev, [item.cid]: true }));
      showHubToast(__("Reward claimed"), __("Added to your balance"));
    },
    [onChallengeAction, actionBusy, showActionError]
  );

  const handleDetailCta = useCallback(async () => {
    if (!selectedChallenge) return;
    const wantsClaim =
      typeof selectedChallenge.claimable === "boolean"
        ? selectedChallenge.claimable
        : selectedChallenge.progDone ||
          String(selectedChallenge.cta || "").toLowerCase().includes("claim");

    // "Play now" navigates when the mission carries a deeplink (normalizer
    // guarantees a same-site path); missions without one keep the toast.
    const launchCta = () => {
      if (selectedChallenge.ctaRoute) {
        closeDetailModal();
        history.push(selectedChallenge.ctaRoute);
        return;
      }
      showHubToast(__("Game launching"), `${__("Opening")} ${selectedChallenge.game}…`);
    };

    if (onChallengeAction) {
      if (actionBusy) return;
      if (selectedChallenge.locked) {
        setActionBusy(true);
        const result = await onChallengeAction(selectedCid, "opt-in");
        setActionBusy(false);
        if (result?.ok) {
          showHubToast(__("Challenge joined"), __("Your progress now counts"));
        } else {
          showActionError(result?.message, __("Could not join"));
        }
        return;
      }
      if (wantsClaim) {
        setActionBusy(true);
        const result = await onChallengeAction(selectedCid, "claim");
        setActionBusy(false);
        if (result?.ok) {
          showHubToast(
            __("Reward claimed"),
            rewardSummary(selectedChallenge.rewards) || __("Added to your balance")
          );
          closeDetailModal();
        } else {
          showActionError(result?.message, __("Claim failed"));
        }
        return;
      }
      launchCta();
      return;
    }

    if (selectedChallenge.locked) {
      showHubToast(__("VIP required"), __("Reach Diamond tier to unlock"));
      return;
    }
    if (wantsClaim) {
      showHubToast(__("Reward claimed"), selectedChallenge.cta);
      return;
    }
    launchCta();
  }, [
    selectedChallenge,
    selectedCid,
    onChallengeAction,
    actionBusy,
    showActionError,
    closeDetailModal,
    history,
  ]);

  // A refreshed hub can drop the selected challenge (e.g. claimed and gone);
  // close the detail modal instead of leaving an empty shell.
  useEffect(() => {
    if (isDetailModalOpen && selectedCid && !challenges[selectedCid]) {
      closeDetailModal();
    }
  }, [isDetailModalOpen, selectedCid, challenges, closeDetailModal]);

  useEffect(() => {
    document.body.classList.toggle("modal-open", isDetailModalOpen);
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isDetailModalOpen]);

  useEffect(() => {
    if (!isDetailModalOpen) return undefined;
    const focusTimeout = window.setTimeout(() => {
      if (modalCloseBtnRef.current) {
        modalCloseBtnRef.current.focus();
      }
    }, 80);
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeDetailModal();
        return;
      }
      if (e.key !== "Tab") return;
      const shell = modalShellRef.current;
      if (!shell) return;
      const focusables = Array.from(
        shell.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimeout);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isDetailModalOpen, closeDetailModal]);

  useEffect(() => {
    if (!isDetailModalOpen || prefersReducedMotion()) return;
    const shell = modalShellRef.current;
    if (!shell) return;
    const bars = shell.querySelectorAll(".detail-prog .bar b");
    bars.forEach((bar) => {
      const w = bar.style.width;
      bar.style.width = "0%";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          bar.style.width = w;
        });
      });
    });
  }, [selectedCid, isDetailModalOpen]);

  const ringOffset = RING_CIRC * (1 - (streak.pct || 0));

  return (
    <div className="wheel_container mission-hub container-xxl">
      <HubToast />
      <div className="page mission-hub__inner">
        {preview && (
          <p className="mission-hub__preview-note" role="status">
            {__("Preview mode — mission data will update when the challenges API is live.")}
          </p>
        )}

        <section className="vip_banner hub">
          <div className="ttl">
            <div className="icon">
              <BoltIcon size={24} />
            </div>
            <div>
              <h1>
                <span className="gold">{__("Mission")}</span> {__("Hub")}
              </h1>
              <div className="sub">
                {__("Complete in-game missions")} · {__("earn free spins, Clover Coins & VIP points")}
              </div>
            </div>
          </div>
          {header.playerName ? (
            <div className="greeting">
              {__("Welcome back,")}{" "}
              <b style={{ color: "var(--t-hi)" }}>{header.playerName}</b>
            </div>
          ) : (
            <div className="greeting" aria-hidden="true" />
          )}
          <div className="metric">
            <div className="v gold">{tabCounts.active}</div>
            <div className="l">{__("Active")}</div>
          </div>
          <div className="metric">
            <div className="v win">{tabCounts.claim}</div>
            <div className="l">{__("Ready to claim")}</div>
          </div>
          <div className="metric">
            <div className="v gold">{header.fsThisWeek}</div>
            <div className="l">{__("Free spins this week")}</div>
          </div>
          <div className="metric">
            <div className="v flame">
              <span className="flame">
                <FlameIcon size={16} />
              </span>
              {header.streakDays}
            </div>
            <div className="l">{__("Day streak")}</div>
          </div>
        </section>

        {featured && (() => {
          const fi = featured.item;
          const kindLabel =
            featured.kind === "claim"
              ? __("Ready to claim")
              : featured.kind === "urgent"
              ? __("Ending soon")
              : __("Almost there");
          const isFeaturedClaim =
            featured.kind === "claim" && !claimedIds[fi.cid];
          return (
            <section
              className={`vip_club_area mission-featured ${featured.kind}`}
              aria-label={__("Next best action")}
            >
              <div
                className="mission-featured__art"
                style={{ background: fi.artBg }}
                aria-hidden="true"
              />
              <div className="mission-featured__body">
                <span className="mission-featured__kind">{kindLabel}</span>
                <h2 className="mission-featured__title">{fi.title}</h2>
                <p className="mission-featured__desc">{fi.description}</p>
                <div className="mission-featured__meta">
                  <RewardLine rewards={fi.rewards} />
                  <div
                    className="mission-featured__prog"
                    role="progressbar"
                    aria-valuenow={Math.round(fi.progPct || 0)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${__("Progress")}: ${fi.progLabel}`}
                  >
                    <b style={{ width: `${fi.progPct || 0}%` }} />
                  </div>
                </div>
              </div>
              <div className="mission-featured__cta">
                {isFeaturedClaim ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={(e) => handleClaim(e, fi)}
                    disabled={actionBusy}
                  >
                    {__("Claim reward")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={(e) => selectItem(fi, e.currentTarget)}
                  >
                    {__("View mission")}
                  </button>
                )}
              </div>
            </section>
          );
        })()}

        <div className="hub-grid">
          <section className="vip_club_area log">
            <div className="log-hd">
              <div className="log-tabs" role="tablist" aria-label={__("Filter challenges")}>
                {LOG_TABS.map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      type="button"
                      key={tab.key}
                      role="tab"
                      id={`mission-log-tab-${tab.key}`}
                      aria-selected={isActive}
                      aria-controls="mission-log-panel"
                      className={`log-tab ${isActive ? "on" : ""}`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => setActiveTab(tab.key)}
                      onKeyDown={(e) => {
                        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
                        e.preventDefault();
                        const idx = LOG_TABS.findIndex((t) => t.key === activeTab);
                        const next =
                          e.key === "ArrowRight"
                            ? (idx + 1) % LOG_TABS.length
                            : (idx - 1 + LOG_TABS.length) % LOG_TABS.length;
                        setActiveTab(LOG_TABS[next].key);
                      }}
                    >
                      {tab.label}
                      <span className="ct">{tabCounts[tab.key] ?? 0}</span>
                    </button>
                  );
                })}
              </div>
              <label className="log-sort">
                <span className="log-sort__label">{__("Sort")}</span>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  aria-label={__("Sort challenges")}
                >
                  <option value="ending">{__("Ending soon")}</option>
                  <option value="reward">{__("Highest reward")}</option>
                  <option value="closest">{__("Closest to done")}</option>
                  <option value="newest">{__("Newest")}</option>
                </select>
              </label>
            </div>
            <div className="log-list" id="mission-log-panel" role="tabpanel" aria-labelledby={`mission-log-tab-${activeTab}`}>
              {filteredItems.length === 0 && (() => {
                const meta = {
                  claim: {
                    ico: <GiftIcon size={26} />,
                    title: __("No rewards waiting"),
                    desc: __("Complete an active mission to earn rewards."),
                    ctaLabel: __("View active missions"),
                    onCta: () => setActiveTab("active"),
                  },
                  new: {
                    ico: <CloverIcon size={26} />,
                    title: __("You're all caught up"),
                    desc: __("New missions arrive regularly — a fresh spin could unlock one."),
                    ctaLabel: __("Browse games"),
                    onCta: () => history.push("/games/all"),
                  },
                  done: {
                    ico: <CloverIcon size={26} />,
                    title: __("Nothing completed yet"),
                    desc: __("Your first claimed mission lands here."),
                  },
                  active: {
                    ico: <BoltIcon size={26} />,
                    title: __("No active missions"),
                    desc: __("Join a mission to start earning."),
                  },
                }[activeTab] || {
                  ico: <CloverIcon size={26} />,
                  title: __("No challenges here yet"),
                  desc: __("Try a different category."),
                };
                return (
                  <div className="log-empty">
                    <div className="log-empty__ico" aria-hidden="true">{meta.ico}</div>
                    <div className="log-empty__title">{meta.title}</div>
                    <div className="log-empty__desc">{meta.desc}</div>
                    {meta.ctaLabel && (
                      <button
                        type="button"
                        className="btn btn-sm log-empty__cta"
                        onClick={meta.onCta}
                      >
                        {meta.ctaLabel}
                      </button>
                    )}
                  </div>
                );
              })()}
              {filteredItems.map((item) => {
                const isClaim = item.state === "claim" && !claimedIds[item.cid];
                const isNear =
                  !isClaim && item.state === "active" && (item.progPct || 0) >= 80;
                const isDone = item.state === "done";
                const itemClass = [
                  "log-item",
                  selectedCid === item.cid ? "sel" : "",
                  isClaim ? "claim" : "",
                  isNear ? "near" : "",
                  isDone ? "done" : "",
                  item.locked || item.state === "locked" ? "locked" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={item.cid}
                    className={itemClass}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => selectItem(item, e.currentTarget)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectItem(item, e.currentTarget);
                      }
                    }}
                  >
                    <div
                      className="log-art"
                      style={{ background: item.artBg }}
                      aria-hidden="true"
                    >
                      {isDone && <span className="log-art__seal">✓</span>}
                    </div>
                    <div className="log-meta">
                      <div className="row1">
                        <TierDot tier={item.tier} />
                        <span className="tt">{item.title}</span>
                      </div>
                      <div className="ds">{item.description}</div>
                      {isNear && (
                        <div className="log-near">{__("Almost there — keep going!")}</div>
                      )}
                      <div className="row3">
                        <div
                          className={`log-prog ${item.progDone ? "done" : ""}`}
                          role="progressbar"
                          aria-valuenow={Math.round(item.progPct || 0)}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={`${item.title}: ${item.progLabel}`}
                        >
                          <b style={{ width: `${item.progPct}%` }} />
                        </div>
                        <span className="log-pct">{item.progLabel}</span>
                      </div>
                    </div>
                    <div className="log-right">
                      <div className="log-rew">
                        <RewardLine rewards={item.rewards} />
                        <small>{item.rewardSmall}</small>
                      </div>
                      {isClaim ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm log-claim-btn"
                          onClick={(e) => handleClaim(e, item)}
                          disabled={actionBusy}
                          aria-label={`${__("Claim reward for")} ${item.title}`}
                        >
                          {__("Claim")}
                        </button>
                      ) : (
                        item.time && (
                          <div className={`log-time ${item.timeUrgent ? "urgent" : ""}`}>
                            {item.time}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {liveClaims && liveClaims.length > 0 && (
          <section className="vip_club_area claims-strip claims-strip-section">
            <div className="section-title">
              <h2>{__("Recent wins")}</h2>
              <small>{__("Updated live")}</small>
            </div>
            <ul className="claims-row" aria-label={__("Recent community wins")}>
              {liveClaims.map((c, i) => (
                <li className="claim-card" key={`${c.who}-${i}`}>
                  <div className="av" aria-hidden="true">{c.avatar}</div>
                  <div className="info">
                    <div className="who">{c.who}</div>
                    <div className="what">{c.what}</div>
                  </div>
                  <div className="amt">{c.amt}</div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      {isDetailModalOpen && selectedChallenge && (
      <div
        className="detail-modal-backdrop open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mission-detail-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeDetailModal();
          }
        }}
      >
        <div className="detail-modal-shell" role="document" ref={modalShellRef}>
          <button
            ref={modalCloseBtnRef}
            type="button"
            className="detail-modal-close"
            onClick={closeDetailModal}
            aria-label={__("Close")}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="3" y1="3" x2="13" y2="13" />
              <line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
          <DetailPanel
            challenge={selectedChallenge}
            onCta={handleDetailCta}
            canOptIn={!!onChallengeAction}
          />
          <section className="vip_club_area streak-ring">
            <div className="ring-wrap">
              <svg className="ring-svg" viewBox="0 0 120 120" aria-hidden="true">
                <defs>
                  <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#fff4cc" />
                    <stop offset="50%" stopColor="#f8d870" />
                    <stop offset="100%" stopColor="#a87b2e" />
                  </linearGradient>
                </defs>
                <circle className="ring-bg" cx="60" cy="60" r="54" />
                <circle
                  className="ring-fg"
                  cx="60"
                  cy="60"
                  r="54"
                  style={{ strokeDashoffset: ringOffset }}
                />
              </svg>
              <div className="ring-center">
                <span className="flame">
                  <FlameIcon size={18} />
                </span>
                <div className="n">{streak.currentDays}</div>
                <div className="l">{__("Day streak")}</div>
              </div>
            </div>
            <div className="streak-text">
              <h4>
                {streak.daysToMega} {__("days to mega prize")}
              </h4>
              <p className="desc">
                {__(
                  "Play any qualifying game today to keep your streak alive. Hit 7 in a row for the"
                )}{" "}
                <b style={{ color: "var(--gold-top)" }}>{streak.megaPrizeLabel}</b> {__("jackpot.")}
              </p>
              <span className="next-rew">
                {__("Today's reward")} · {streak.todayReward}
              </span>
            </div>
          </section>
        </div>
      </div>
      )}
    </div>
  );
}
